import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  Button,
  Chip,
  Avatar,
  Rating,
  Alert,
  CircularProgress,
  Divider,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
} from '@mui/material';
import {
  Person as PersonIcon,
  Schedule as TimeIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Share as ShareIcon,
  AttachMoney as BudgetIcon,
  Luggage as LuggageIcon,
  ChildCare as ChildSeatIcon,
  Accessible as WheelchairIcon,
  Chat as ChatIcon,
  LocationOn as LocationIcon,
  AccessTime as FlexIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useLoading } from '../../contexts/LoadingContext';
import apiService from '../../services/apiService';
import { useUserProfile } from '../../contexts/UserProfileContext';
import { useSocket } from '../../contexts/SocketContext';
import TranslatingText from '../../components/Common/TranslatingText';
import SocialShareModal from '../../components/Common/SocialShareModal';

interface RideRequest {
  id: string;
  passenger: {
    id: string;
    firstName: string;
    lastName: string;
    preferredName?: string;
    passengerRating?: number;
    profilePicture?: string;
  };
  originCity: {
    id: string;
    name: string;
    province: string;
  };
  destinationCity: {
    id: string;
    name: string;
    province: string;
  };
  originDetails?: string;
  destinationDetails?: string;
  preferredDateTime: string;
  timeFlexibility: number;
  passengerCount: number;
  minBudget?: number;
  maxBudget?: number;
  needsLargeLuggage?: boolean;
  needsChildSeat?: boolean;
  needsWheelchairAccess?: boolean;
  specialRequirements?: string;
  description?: string;
  status: string;
  allowChat?: boolean;
  expiresAt: string;
  bids?: Array<{
    id: string;
    priceOffer: number;
    status: string;
    message?: string;
    createdAt?: string;
    driver: {
      id: string;
      firstName: string;
      lastName: string;
      preferredName?: string;
    };
  }>;
}

const BrowseRequestsPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { setLoading } = useLoading();
  const { t } = useTranslation();
  const { openProfile } = useUserProfile();

  const [requests, setRequests] = useState<RideRequest[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [requestBids, setRequestBids] = useState<{ [key: string]: { price: string; message: string; submitting: boolean; error: string } }>({});
  const [shareModalRequest, setShareModalRequest] = useState<RideRequest | null>(null);
  const [detailRequest, setDetailRequest] = useState<RideRequest | null>(null);

  const isDriver = user?.role === 'DRIVER' || user?.isDriver === true;

  const handleProfileClick = (e: React.MouseEvent, userId: string) => {
    e.stopPropagation();
    openProfile(userId);
  };

  const handleBecomeDriverRedirect = () => {
    const returnUrl = location.pathname + location.search;
    navigate('/become-driver', { state: { returnUrl } });
  };

  const loadRequests = async () => {
    setIsLoading(true);
    setError('');
    setLoading(true, 'Loading ride requests...');

    try {
      const response = await apiService.get('/requests/public-search', {
        status: 'OPEN',
        sortBy: 'preferredDateTime',
      });

      if (response.success) {
        const items = response.data.requests || response.data.data?.requests || [];
        setRequests(items);

        // Auto-open requested ride if requestId or id is in the URL search parameters
        const queryParams = new URLSearchParams(location.search);
        const reqId = queryParams.get('requestId') || queryParams.get('id');
        if (reqId) {
          const found = items.find((r: RideRequest) => r.id === reqId);
          if (found) {
            setDetailRequest(found);
          } else {
            // Fetch request details directly if not in the search results
            try {
              const detailRes = await apiService.get(`/requests/${reqId}`);
              if (detailRes.success && detailRes.data) {
                setDetailRequest(detailRes.data.request || detailRes.data);
              }
            } catch (err) {
              console.error('Failed to fetch request details for auto-open:', err);
            }
          }
          // Clear query params to keep URL clean and prevent repeating auto-open
          navigate(location.pathname, { replace: true });
        }
      }
    } catch (error: any) {
      console.error('Load requests error:', error);
      setError('Failed to load ride requests. Please try again.');
    } finally {
      setIsLoading(false);
      setLoading(false);
    }
  };

  const { socket } = useSocket();

  useEffect(() => {
    loadRequests();
  }, []);

  useEffect(() => {
    if (!socket) return;

    const handleUpdate = () => {
      loadRequests();
    };

    socket.on('ride_request_created', handleUpdate);
    socket.on('ride_request_updated', handleUpdate);
    socket.on('ride_request_cancelled', handleUpdate);
    socket.on('new_ride_request', handleUpdate);
    socket.on('bid_created', handleUpdate);
    socket.on('bid_updated', handleUpdate);

    return () => {
      socket.off('ride_request_created', handleUpdate);
      socket.off('ride_request_updated', handleUpdate);
      socket.off('ride_request_cancelled', handleUpdate);
      socket.off('new_ride_request', handleUpdate);
      socket.off('bid_created', handleUpdate);
    };
  }, [socket]);

  useEffect(() => {
    if (detailRequest) {
      const updatedDetail = requests.find(r => r.id === detailRequest.id);
      if (updatedDetail) {
        setDetailRequest(updatedDetail);
      } else {
        setDetailRequest(null);
      }
    }
  }, [requests]);


  const handleSubmitBid = async (request: RideRequest) => {
    const bidState = requestBids[request.id] || { price: '', message: '', submitting: false, error: '' };
    const price = parseFloat(bidState.price);

    if (isNaN(price) || price <= 0) {
      setRequestBids(prev => ({ ...prev, [request.id]: { ...bidState, error: 'Please enter a valid price' } }));
      return;
    }
    // Budget is now a suggestion and doesn't need to be strictly enforced on the frontend

    setRequestBids(prev => ({ ...prev, [request.id]: { ...bidState, submitting: true, error: '' } }));

    try {
      const response = await apiService.post('/bids', {
        requestId: request.id,
        priceOffer: price,
        message: bidState.message.trim() || undefined,
      });

      if (response.success) {
        setRequestBids(prev => {
          const next = { ...prev };
          delete next[request.id];
          return next;
        });
        setDetailRequest(null);
        loadRequests();
      }
    } catch (error: any) {
      console.error('Submit bid error:', error);
      setRequestBids(prev => ({
        ...prev,
        [request.id]: {
          ...bidState,
          submitting: false,
          error: error.response?.data?.message || 'Failed to submit bid',
        },
      }));
    }
  };

  const formatDateTime = (dateTime: string) => {
    const date = new Date(dateTime);
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };
  };

  /* ─── Compact preview card ─── */
  const renderRequestCard = (request: RideRequest) => {
    const { date, time } = formatDateTime(request.preferredDateTime);
    const myBid = isDriver && request.bids?.find(bid => bid.driver.id === user?.id);
    const hasExistingBid = !!myBid;
    const requestBid = requestBids[request.id] || { price: '', message: '', submitting: false, error: '' };

    return (
      <Card
        key={request.id}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          transition: 'all 0.25s ease',
          '&:hover': { boxShadow: 6, transform: 'translateY(-2px)' },
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
          cursor: 'pointer',
        }}
      >
        <CardActionArea
          onClick={() => setDetailRequest(request)}
          sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
        >
          <CardContent sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>

            {/* Route header */}
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3, fontSize: '0.95rem', mb: 1 }}>
              {request.originCity.name} → {request.destinationCity.name}
            </Typography>

            {/* Passenger */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Avatar
                src={request.passenger.profilePicture}
                sx={{ width: 26, height: 26, fontSize: '0.7rem' }}
              >
                {(request.passenger.preferredName || request.passenger.firstName || '?')[0]}{(request.passenger.lastName || '?')[0]}
              </Avatar>
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', lineHeight: 1.1 }}>
                  {request.passenger.preferredName || request.passenger.firstName} {request.passenger.lastName[0]}.
                </Typography>
                {request.passenger.passengerRating && (
                  <Rating value={request.passenger.passengerRating} readOnly size="small" precision={0.1}
                    sx={{ fontSize: '0.65rem' }} />
                )}
              </Box>
            </Box>

            <Divider sx={{ mb: 1 }} />

            {/* Date / pax */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.4 }}>
              <TimeIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">{date} · {time}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <PersonIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {request.passengerCount} passenger{request.passengerCount > 1 ? 's' : ''}
              </Typography>
            </Box>

            {/* Budget + status chips */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 'auto' }}>
              {request.minBudget || request.maxBudget ? (
                <Chip
                  label={request.minBudget && request.maxBudget
                    ? `Suggested: $${request.minBudget}–$${request.maxBudget}`
                    : `Suggested: Up to $${request.maxBudget}`}
                  size="small" color="secondary" variant="outlined"
                  sx={{ fontWeight: 700, fontSize: '0.7rem' }}
                />
              ) : (
                <Chip label="Budget TBD" size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
              )}
              {hasExistingBid
                ? <Chip label="Bid Sent" color="success" size="small" />
                : <Chip label={request.status} color={request.status === 'OPEN' ? 'success' : 'default'}
                  variant={request.status === 'OPEN' ? 'filled' : 'outlined'} size="small" />
              }
              {request.bids && request.bids.length > 0 && (
                <Chip label={`${request.bids.length} bid${request.bids.length > 1 ? 's' : ''}`}
                  size="small" variant="outlined" color="info" sx={{ fontSize: '0.7rem' }} />
              )}
            </Box>

            {/* Special needs icons */}
            {(request.needsLargeLuggage || request.needsChildSeat || request.needsWheelchairAccess) && (
              <Box sx={{ display: 'flex', gap: 0.5, mt: 1 }}>
                {request.needsLargeLuggage && <LuggageIcon sx={{ fontSize: 15, color: 'text.secondary' }} titleAccess="Large luggage" />}
                {request.needsChildSeat && <ChildSeatIcon sx={{ fontSize: 15, color: 'text.secondary' }} titleAccess="Child seat" />}
                {request.needsWheelchairAccess && <WheelchairIcon sx={{ fontSize: 15, color: 'text.secondary' }} titleAccess="Wheelchair access" />}
              </Box>
            )}

            <Typography variant="caption" color="primary.main" sx={{ mt: 1, display: 'block', fontWeight: 500 }}>
              Tap for full details →
            </Typography>
          </CardContent>
        </CardActionArea>

        {/* ── Inline bid section (outside CardActionArea so clicks don't open modal) ── */}
        {request.status === 'OPEN' && (
          <Box
            sx={{ px: 2, pb: 2, pt: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <Divider sx={{ mb: 1.5 }} />
            {isDriver ? (
              !hasExistingBid ? (
                <Box>
                  {requestBid.error && (
                    <Alert severity="error" sx={{ mb: 1, py: 0, fontSize: '0.72rem' }}>{requestBid.error}</Alert>
                  )}
                  <Grid container spacing={1} alignItems="center">
                    <Grid size={{ xs: 5 }}>
                      <TextField
                        label="Offer $"
                        type="number"
                        fullWidth
                        size="small"
                        value={requestBid.price}
                        onChange={(e) => setRequestBids(prev => ({
                          ...prev,
                          [request.id]: { ...requestBid, price: e.target.value, error: '' }
                        }))}
                        disabled={requestBid.submitting}
                        inputProps={{ style: { padding: '5px 8px' } }}
                      />
                    </Grid>
                    <Grid size={{ xs: 7 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        size="small"
                        onClick={() => handleSubmitBid(request)}
                        disabled={requestBid.submitting || !requestBid.price}
                      >
                        {requestBid.submitting ? <CircularProgress size={16} color="inherit" /> : 'Send Bid'}
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              ) : (
                <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
                  ✓ You've already bid ${myBid ? `$${myBid.priceOffer.toFixed(2)}` : ''} on this request.
                </Typography>
              )
            ) : user ? (
              <Button size="small" variant="outlined" fullWidth onClick={handleBecomeDriverRedirect}
                sx={{ fontSize: '0.72rem', py: 0.4 }}>
                Become a Driver to Bid
              </Button>
            ) : (
              <Button size="small" variant="outlined" fullWidth
                onClick={() => navigate('/login', { state: { redirect: '/browse-requests' } })}
                sx={{ fontSize: '0.72rem', py: 0.4 }}>
                Log In to Bid
              </Button>
            )}
          </Box>
        )}
      </Card>
    );
  };

  /* ─── Full detail modal ─── */
  const renderDetailModal = () => {
    if (!detailRequest) return null;
    const req = detailRequest;
    const { date, time } = formatDateTime(req.preferredDateTime);
    const expiresDate = new Date(req.expiresAt).toLocaleDateString();
    const myBid = isDriver && req.bids?.find(bid => bid.driver.id === user?.id);
    const hasExistingBid = !!myBid;
    const requestBid = requestBids[req.id] || { price: '', message: '', submitting: false, error: '' };
    const bidPrices = req.bids?.map(b => b.priceOffer) || [];
    const minBid = bidPrices.length > 0 ? Math.min(...bidPrices) : null;
    const maxBid = bidPrices.length > 0 ? Math.max(...bidPrices) : null;

    const InfoRow = ({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) => (
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 0.8 }}>
        <Box sx={{ color: 'primary.main', mt: 0.1, flexShrink: 0 }}>{icon}</Box>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1 }}>{label}</Typography>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>{value}</Typography>
        </Box>
      </Box>
    );

    return (
      <Dialog
        open={!!detailRequest}
        onClose={() => setDetailRequest(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        {/* Coloured header bar */}
        <Box sx={{ bgcolor: 'primary.main', px: 3, py: 2, color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="overline" sx={{ opacity: 0.8, lineHeight: 1 }}>Ride Request</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {req.originCity.name} → {req.destinationCity.name}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              {req.originCity.province} → {req.destinationCity.province}
            </Typography>
          </Box>
          <IconButton onClick={() => setDetailRequest(null)} sx={{ color: 'white', mt: -0.5 }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ px: 3, pt: 2, pb: 1 }}>

          {/* Passenger info */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Avatar
              src={req.passenger.profilePicture}
              sx={{ width: 48, height: 48, cursor: 'pointer', border: '2px solid', borderColor: 'primary.light' }}
              onClick={(e) => handleProfileClick(e, req.passenger.id)}
            >
              {(req.passenger.preferredName || req.passenger.firstName || '?')[0]}{(req.passenger.lastName || '?')[0]}
            </Avatar>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                {req.passenger.preferredName || req.passenger.firstName} {req.passenger.lastName[0]}.
              </Typography>
              {req.passenger.passengerRating && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Rating value={req.passenger.passengerRating} readOnly size="small" precision={0.1} />
                  <Typography variant="caption" color="text.secondary">({req.passenger.passengerRating})</Typography>
                </Box>
              )}
            </Box>
            <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
              {hasExistingBid
                ? <Chip label="Bid Sent" color="success" size="small" />
                : <Chip label={req.status} color={req.status === 'OPEN' ? 'success' : 'default'}
                  variant={req.status === 'OPEN' ? 'filled' : 'outlined'} size="small" />
              }
            </Box>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Details grid */}
          <Stack spacing={0} divider={<Divider flexItem />}>
            <InfoRow icon={<TimeIcon fontSize="small" />} label="Preferred Date & Time"
              value={`${date} at ${time}`} />

            {req.timeFlexibility > 0 && (
              <InfoRow icon={<FlexIcon fontSize="small" />} label="Time Flexibility"
                value={`±${req.timeFlexibility} hour${req.timeFlexibility > 1 ? 's' : ''}`} />
            )}

            <InfoRow icon={<PersonIcon fontSize="small" />} label="Passengers"
              value={`${req.passengerCount} passenger${req.passengerCount > 1 ? 's' : ''}`} />

            <InfoRow icon={<BudgetIcon fontSize="small" />} label="Suggested Budget"
              value={
                req.minBudget && req.maxBudget
                  ? `$${req.minBudget} – $${req.maxBudget} CAD`
                  : req.maxBudget
                    ? `Up to $${req.maxBudget} CAD`
                    : 'Not specified'
              }
            />

            {req.originDetails && (
              <InfoRow icon={<LocationIcon fontSize="small" />} label="Pickup Details"
                value={req.originDetails} />
            )}
            {req.destinationDetails && (
              <InfoRow icon={<LocationIcon fontSize="small" />} label="Drop-off Details"
                value={req.destinationDetails} />
            )}

            <InfoRow icon={<TimeIcon fontSize="small" />} label="Request Expires"
              value={expiresDate} />
          </Stack>

          {/* Special requirements */}
          {(req.needsLargeLuggage || req.needsChildSeat || req.needsWheelchairAccess || req.allowChat) && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                SPECIAL REQUIREMENTS
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {req.needsLargeLuggage && <Chip icon={<LuggageIcon />} label="Large Luggage" size="small" variant="outlined" />}
                {req.needsChildSeat && <Chip icon={<ChildSeatIcon />} label="Child Seat" size="small" variant="outlined" />}
                {req.needsWheelchairAccess && <Chip icon={<WheelchairIcon />} label="Wheelchair Access" size="small" variant="outlined" />}
                {req.allowChat && <Chip icon={<ChatIcon />} label="Chat Allowed" size="small" variant="outlined" color="primary" />}
              </Box>
            </Box>
          )}

          {/* Extra notes */}
          {req.description && (
            <Box sx={{ mt: 2, bgcolor: 'grey.50', p: 2, borderRadius: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                PASSENGER'S NOTE
              </Typography>
              <TranslatingText text={req.description} variant="body2" />
            </Box>
          )}

          {req.specialRequirements && (
            <Box sx={{ mt: 1.5, bgcolor: 'warning.50', p: 2, borderRadius: 2, border: '1px solid', borderColor: 'warning.200' }}>
              <Typography variant="caption" color="warning.dark" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                ADDITIONAL REQUIREMENTS
              </Typography>
              <Typography variant="body2">{req.specialRequirements}</Typography>
            </Box>
          )}

          {/* Existing bids summary (driver can see their own section) */}
          {req.bids && req.bids.length > 0 && (
            <Box sx={{ mt: 3, mb: 2 }}>
              <Typography color="text.primary" sx={{ fontWeight: 800, fontSize: '1.5rem', lineHeight: 1.2 }}>
                {req.bids.length} BID{req.bids.length > 1 ? 'S' : ''} PLACED
              </Typography>
              {minBid !== null && maxBid !== null && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 500 }}>
                  {minBid === maxBid
                    ? `Current Offer: $${minBid.toFixed(2)} CAD`
                    : `Active Offer Range: $${minBid.toFixed(2)} – $${maxBid.toFixed(2)} CAD`
                  }
                </Typography>
              )}
            </Box>
          )}

          {myBid && (
            <Box sx={{
              mt: 2,
              mb: 3,
              p: 2.5,
              borderRadius: 3,
              border: '1px solid',
              borderColor: myBid.status === 'ACCEPTED' ? 'success.light' : myBid.status === 'REJECTED' ? 'error.light' : 'primary.light',
              bgcolor: myBid.status === 'ACCEPTED' ? 'success.50' : myBid.status === 'REJECTED' ? 'error.50' : 'primary.50',
              boxShadow: '0 4px 12px rgba(0,0,0,0.03)'
            }}>
              <Typography variant="subtitle2" sx={{
                fontWeight: 700,
                mb: 1.5,
                color: myBid.status === 'ACCEPTED' ? 'success.dark' : myBid.status === 'REJECTED' ? 'error.dark' : 'primary.dark',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <span>YOUR OFFER DETAILED</span>
                <Chip
                  label={myBid.status}
                  size="small"
                  color={myBid.status === 'ACCEPTED' ? 'success' : myBid.status === 'REJECTED' ? 'error' : 'primary'}
                  sx={{ fontWeight: 700, fontSize: '0.75rem' }}
                />
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>OFFER AMOUNT</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 800, fontSize: '1.25rem' }}>
                    ${myBid.priceOffer.toFixed(2)} CAD
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>SUBMITTED ON</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {myBid.createdAt ? new Date(myBid.createdAt).toLocaleDateString() : 'N/A'}
                  </Typography>
                </Grid>
                {myBid.message && (
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>YOUR MESSAGE</Typography>
                    <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.primary', mt: 0.5 }}>
                      "{myBid.message}"
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}

          {/* Bid form section */}
          {req.status === 'OPEN' && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 2 }} />
              {isDriver ? (
                !hasExistingBid ? (
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
                      Make an Offer
                    </Typography>
                    {requestBid.error && (
                      <Alert severity="error" sx={{ mb: 1.5 }}>{requestBid.error}</Alert>
                    )}
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 5 }}>
                        <TextField
                          label="Your Price Offer (CAD)"
                          type="number"
                          fullWidth
                          size="small"
                          value={requestBid.price}
                          onChange={(e) => setRequestBids(prev => ({
                            ...prev,
                            [req.id]: { ...requestBid, price: e.target.value, error: '' }
                          }))}
                          disabled={requestBid.submitting}
                          InputProps={{
                            startAdornment: <Typography variant="body2" sx={{ mr: 0.5 }}>$</Typography>,
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 7 }}>
                        <TextField
                          label="Message to Passenger (optional)"
                          fullWidth
                          size="small"
                          value={requestBid.message}
                          onChange={(e) => setRequestBids(prev => ({
                            ...prev,
                            [req.id]: { ...requestBid, message: e.target.value, error: '' }
                          }))}
                          disabled={requestBid.submitting}
                          placeholder="Introduce yourself..."
                        />
                      </Grid>
                    </Grid>
                  </Box>
                ) : (
                  <Alert severity="success" icon={false}>
                    ✓ You've already bid ${myBid ? `$${myBid.priceOffer.toFixed(2)}` : ''} on this request.
                  </Alert>
                )
              ) : user ? (
                <Alert severity="info" action={
                  <Button color="inherit" size="small" onClick={handleBecomeDriverRedirect}>
                    Become a Driver
                  </Button>
                }>
                  You need to be a verified driver to place bids.
                </Alert>
              ) : (
                <Alert severity="info" action={
                  <Button color="inherit" size="small" onClick={() => navigate('/login', { state: { redirect: '/browse-requests' } })}>
                    Log In
                  </Button>
                }>
                  Please log in to place a bid.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 1, borderTop: '1px solid', borderColor: 'divider' }}>
          <IconButton
            onClick={(e) => { e.stopPropagation(); setShareModalRequest(req); setDetailRequest(null); }}
            title="Share"
            color="primary"
          >
            <ShareIcon />
          </IconButton>
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setDetailRequest(null)} color="inherit">
            Close
          </Button>
          {req.status === 'OPEN' && isDriver && !hasExistingBid && (
            <Button
              variant="contained"
              onClick={() => handleSubmitBid(req)}
              disabled={requestBid.submitting || !requestBid.price}
              startIcon={requestBid.submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {requestBid.submitting ? 'Submitting…' : 'Send Bid'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Box sx={{ py: 6, bgcolor: '#f5f7fa', minHeight: '100vh' }}>
      <Container maxWidth="lg">
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h3" component="h1" sx={{ fontWeight: 800, color: '#1a237e', mb: 1 }}>
              Browse Ride Requests
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
              Find passengers looking for a ride and make them an offer.
            </Typography>
          </Box>
          <Button
            startIcon={<SearchIcon />}
            variant="outlined"
            onClick={loadRequests}
            disabled={isLoading}
            sx={{ borderRadius: 2, px: 3 }}
          >
            Refresh List
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 4 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {isLoading && requests.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress size={60} thickness={4} />
          </Box>
        ) : requests.length > 0 ? (
          <Grid container spacing={2}>
            {requests.map((request) => (
              <Grid key={request.id} size={{ xs: 12, sm: 6, md: 4 }}>
                {renderRequestCard(request)}
              </Grid>
            ))}
          </Grid>
        ) : !isLoading && (
          <Paper sx={{ p: 10, textAlign: 'center', borderRadius: 4, bgcolor: 'background.paper', boxShadow: 1 }}>
            <Typography variant="h5" color="text.secondary" gutterBottom>
              No active ride requests found
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Check back later or try refreshing the list.
            </Typography>
            <Button variant="contained" onClick={loadRequests}>
              Refresh Now
            </Button>
          </Paper>
        )}
      </Container>

      {/* Detail modal */}
      {renderDetailModal()}

      {/* Share modal */}
      <SocialShareModal
        open={!!shareModalRequest}
        onClose={() => setShareModalRequest(null)}
        type="request"
        data={shareModalRequest || {}}
      />
    </Box>
  );
};

export default BrowseRequestsPage;